/**
 * Linelits.
 *
 * The currency, and it is not an abstraction: a linelit *is* a unit of line.
 * They come into existence when a cut leaves an offcut too short to be a line
 * — see `MIN_LINE_LENGTH` — so the money supply is literally the swarf from
 * building ships. That is why the fabricator can turn them back into line at no
 * charge: it is not manufacturing, it is re-melting.
 *
 * ── Carried and banked ──────────────────────────────────────────────────────
 *
 * Two piles, and the difference between them is the only thing that makes
 * carrying money a decision. What is on the ship goes with the ship: lose the
 * hull and it is gone. What is banked is safe anywhere, because a station's
 * vault is a station's vault whichever one you are standing in.
 *
 * So the balance to strike is between having enough on you to buy what you find
 * and not having so much that a bad approach costs you a fortune.
 */
export const EMPTY_WALLET = { carried: 0, banked: 0 };
/**
 * Comparison slack, in linelits.
 *
 * Floating-point money that came from summing offcuts will not land exactly on
 * a price, and refusing a purchase because the player has 0.29999999999999993
 * of the 0.3 they need is the kind of bug nobody can report usefully. A
 * hundredth of the smallest unit shown is far below anything visible.
 */
const EPSILON = 1e-6;
/** Two decimals, which is the resolution anything is ever shown at. */
export function formatLinelits(amount) {
    return amount.toFixed(2);
}
export function canAfford(wallet, price) {
    return wallet.carried + EPSILON >= price;
}
/**
 * Spend from what is on the ship.
 *
 * Only ever from `carried` — banked linelits are in a vault, and you have to
 * withdraw before you can spend, which is the whole point of the two piles.
 * Returns null rather than going negative, so a caller cannot half-complete a
 * purchase by ignoring a boolean.
 */
export function spend(wallet, price) {
    if (!canAfford(wallet, price))
        return null;
    return { ...wallet, carried: Math.max(0, wallet.carried - price) };
}
export function earn(wallet, amount) {
    return { ...wallet, carried: wallet.carried + Math.max(0, amount) };
}
/** Ship to vault. Clamped, so "deposit everything" is just a large number. */
export function deposit(wallet, amount) {
    const moved = Math.min(Math.max(0, amount), wallet.carried);
    return { carried: wallet.carried - moved, banked: wallet.banked + moved };
}
/** Vault to ship. */
export function withdraw(wallet, amount) {
    const moved = Math.min(Math.max(0, amount), wallet.banked);
    return { carried: wallet.carried + moved, banked: wallet.banked - moved };
}
/**
 * What is left after losing the hull.
 *
 * Everything on the ship, and nothing in the vault. This is the mechanic the
 * two piles exist for, and it is why a long run without docking is a gamble
 * rather than simply slow.
 */
export function loseCarried(wallet) {
    return { carried: 0, banked: wallet.banked };
}
//# sourceMappingURL=linelits.js.map